// @ts-nocheck
import React, { useState, useRef, useCallback } from 'react';

const _t = () => (window).__ENGPORTAL_THEME || null;
const _dark = { bg: "#0B1120", surface: "#111827", surfaceHover: "#1a2332", border: "#1E293B", borderLight: "#334155", text: "#E2E8F0", textMuted: "#94A3B8", textDim: "#64748B", accent: "#3B82F6", accentGlow: "rgba(59, 130, 246, 0.15)", green: "#10B981", yellow: "#F59E0B", red: "#EF4444", purple: "#8B5CF6", orange: "#F97316", cardShadow: "none", mode: "dark" };
const theme = new Proxy(_dark, { get: (fallback, prop) => { const t = _t(); return t ? t[prop] : fallback[prop]; }});

// ─── Excel parsing using SheetJS loaded from CDN ─────────────────────────────
const loadXLSX = () => {
  return new Promise((resolve, reject) => {
    if (window.XLSX) return resolve(window.XLSX);
    const s = document.createElement('script');
    s.src = 'https://cdn.sheetjs.com/xlsx-0.20.1/package/dist/xlsx.full.min.js';
    s.onload = () => resolve(window.XLSX);
    s.onerror = () => reject(new Error('Failed to load SheetJS'));
    document.head.appendChild(s);
  });
};

const parseExcel = async (file) => {
  const XLSX = await loadXLSX();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
      resolve(data);
    };
    reader.readAsArrayBuffer(file);
  });
};

// ─── Import type configurations ──────────────────────────────────────────────
const IMPORT_TYPES = {
  workorders: {
    label: 'Work Orders / Tasks',
    icon: '📋',
    color: theme.purple,
    description: 'Import tasks from your action tracker spreadsheet',
    expectedColumns: ['Date', 'Customer', 'Action', 'Owner', 'Support', 'Deliver to AvCarb', 'Deliver to Customer', 'Priority', 'Due Date', 'Complete', 'Comments'],
    fieldMap: {
      'Date': { target: 'created_date', type: 'date' },
      'Customer': { target: 'customer_ref', type: 'text' },
      'Action': { target: 'title', type: 'text', required: true },
      'Owner': { target: 'owner_name', type: 'text' },
      'Support': { target: 'support_name', type: 'text' },
      'Deliver to AvCarb': { target: 'deliver_avcarb', type: 'text' },
      'Deliver to Customer': { target: 'deliver_customer', type: 'text' },
      'Priority': { target: 'priority', type: 'priority' },
      'Due Date': { target: 'needed_by', type: 'date' },
      'Complete': { target: 'is_complete', type: 'boolean' },
      'Comments': { target: 'description', type: 'text' },
    },
    buildPayload: (row, mapping) => {
      const get = (col) => {
        const srcCol = Object.entries(mapping).find(([k, v]) => v === col)?.[0];
        return srcCol ? row[srcCol] : '';
      };
      const priorityMap = { 'high': 'high', 'h': 'high', 'urgent': 'urgent', 'u': 'urgent', 'low': 'low', 'l': 'low', 'medium': 'normal', 'm': 'normal', 'normal': 'normal', 'n': 'normal' };
      const rawPriority = (get('priority') || '').toString().toLowerCase().trim();
      const isComplete = ['yes', 'y', 'true', '1', 'x', 'done', 'complete'].includes((get('is_complete') || '').toString().toLowerCase().trim());
      
      let needed_by = get('needed_by');
      if (needed_by instanceof Date) needed_by = needed_by.toISOString().split('T')[0];
      else if (typeof needed_by === 'string' && needed_by) {
        try { needed_by = new Date(needed_by).toISOString().split('T')[0]; } catch(e) { needed_by = null; }
      } else { needed_by = null; }

      const title = get('title');
      if (!title) return null;

      const customerRef = get('customer_ref');
      const ownerName = get('owner_name');
      const comments = get('description');
      const deliverAvcarb = get('deliver_avcarb');
      const deliverCust = get('deliver_customer');
      const supportName = get('support_name');
      
      let desc = '';
      if (customerRef) desc += `Customer: ${customerRef}\n`;
      if (ownerName) desc += `Owner: ${ownerName}\n`;
      if (supportName) desc += `Support: ${supportName}\n`;
      if (deliverAvcarb) desc += `Deliver to AvCarb: ${deliverAvcarb}\n`;
      if (deliverCust) desc += `Deliver to Customer: ${deliverCust}\n`;
      if (comments) desc += `\n${comments}`;

      return {
        title: title.toString().substring(0, 255),
        description: desc.trim(),
        work_type: 'other',
        priority: priorityMap[rawPriority] || 'normal',
        status: isComplete ? 'completed' : 'submitted',
        needed_by: needed_by,
      };
    },
    apiUrl: '/api/workorders/',
  },
  customers: {
    label: 'Customers',
    icon: '🤝',
    color: theme.teal,
    description: 'Import customer/contact list',
    expectedColumns: ['Company Name', 'Code', 'Industry', 'Status', 'Website', 'Address', 'Notes'],
    fieldMap: {
      'Company Name': { target: 'name', type: 'text', required: true },
      'Code': { target: 'code', type: 'text', required: true },
      'Industry': { target: 'industry', type: 'text' },
      'Status': { target: 'status', type: 'text' },
      'Website': { target: 'website', type: 'text' },
      'Address': { target: 'address', type: 'text' },
      'Notes': { target: 'notes', type: 'text' },
    },
    buildPayload: (row, mapping) => {
      const get = (col) => { const s = Object.entries(mapping).find(([k, v]) => v === col)?.[0]; return s ? row[s] : ''; };
      const name = get('name'); const code = get('code');
      if (!name || !code) return null;
      const industryMap = { 'automotive': 'automotive', 'auto': 'automotive', 'aerospace': 'aerospace', 'aero': 'aerospace', 'industrial': 'industrial', 'marine': 'marine', 'energy': 'energy', 'defense': 'defense', 'electronics': 'electronics' };
      const statusMap = { 'active': 'active', 'prospect': 'prospect', 'qualified': 'qualified', 'inactive': 'inactive' };
      return {
        name: name.toString(), code: code.toString().toUpperCase().substring(0, 20),
        industry: industryMap[(get('industry')||'').toLowerCase().trim()] || 'other',
        status: statusMap[(get('status')||'').toLowerCase().trim()] || 'prospect',
        website: (get('website') || '').toString(), address: (get('address') || '').toString(),
        notes: (get('notes') || '').toString(),
      };
    },
    apiUrl: '/api/customers/customers/',
  },
  inventory: {
    label: 'Inventory Parts',
    icon: '📦',
    color: theme.green,
    description: 'Import sample inventory / parts list',
    expectedColumns: ['Part Number', 'Name', 'Quantity', 'Unit', 'Status', 'Description'],
    fieldMap: {
      'Part Number': { target: 'part_number', type: 'text', required: true },
      'Name': { target: 'name', type: 'text', required: true },
      'Quantity': { target: 'quantity', type: 'number' },
      'Unit': { target: 'unit_of_measure', type: 'text' },
      'Status': { target: 'status', type: 'text' },
      'Description': { target: 'material_specification', type: 'text' },
    },
    buildPayload: (row, mapping) => {
      const get = (col) => { const s = Object.entries(mapping).find(([k, v]) => v === col)?.[0]; return s ? row[s] : ''; };
      const pn = get('part_number'); const name = get('name');
      if (!pn || !name) return null;
      let qty = parseFloat(get('quantity')); if (isNaN(qty)) qty = 0;
      return {
        part_number: pn.toString().substring(0, 50), name: name.toString(),
        quantity: qty, unit_of_measure: (get('unit_of_measure') || 'pcs').toString(),
        material_specification: (get('material_specification') || '').toString(),
      };
    },
    apiUrl: '/api/inventory/parts/',
  },
};

// ─── Auto-map columns ────────────────────────────────────────────────────────
const autoMapColumns = (fileColumns, importType) => {
  const mapping = {};
  const cfg = IMPORT_TYPES[importType];
  const targets = Object.entries(cfg.fieldMap);

  fileColumns.forEach(fc => {
    const fcLower = fc.toLowerCase().trim();
    // Try exact match first, then fuzzy
    for (const [expectedCol, fieldCfg] of targets) {
      const expLower = expectedCol.toLowerCase();
      if (fcLower === expLower || fcLower === fieldCfg.target.replace(/_/g, ' ')) {
        mapping[fc] = fieldCfg.target;
        break;
      }
    }
    if (!mapping[fc]) {
      // Fuzzy matching
      for (const [expectedCol, fieldCfg] of targets) {
        const expLower = expectedCol.toLowerCase();
        if (fcLower.includes(expLower) || expLower.includes(fcLower) ||
            fcLower.includes(fieldCfg.target.replace(/_/g, ' '))) {
          if (!Object.values(mapping).includes(fieldCfg.target)) {
            mapping[fc] = fieldCfg.target;
            break;
          }
        }
      }
    }
  });
  return mapping;
};

export default function ImportPage() {
  const iStyle = { width: '100%', padding: '8px 12px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const [importType, setImportType] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [fileName, setFileName] = useState('');
  const [step, setStep] = useState('select'); // select → upload → map → preview → importing → done
  const [importResults, setImportResults] = useState({ success: 0, failed: 0, errors: [] });
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const data = await parseExcel(file);
      if (data.length === 0) return alert('No data found in file');
      const cols = Object.keys(data[0]);
      setFileColumns(cols);
      setRawData(data);
      const auto = autoMapColumns(cols, importType);
      setMapping(auto);
      setStep('map');
    } catch (e) {
      alert('Error parsing file: ' + e.message);
    }
  };

  const updateMapping = (fileCol, targetField) => {
    setMapping(prev => {
      const next = { ...prev };
      if (targetField === '') { delete next[fileCol]; }
      else { next[fileCol] = targetField; }
      return next;
    });
  };

  const cfg = importType ? IMPORT_TYPES[importType] : null;

  const previewPayloads = cfg ? rawData.map(row => cfg.buildPayload(row, mapping)).filter(Boolean) : [];

  const handleImport = async () => {
    setImporting(true);
    setStep('importing');
    const csrfToken = document.cookie.split('csrftoken=')[1]?.split(';')[0] || '';
    let success = 0, failed = 0;
    const errors = [];

    for (let i = 0; i < previewPayloads.length; i++) {
      try {
        const res = await fetch(cfg.apiUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
          body: JSON.stringify(previewPayloads[i]),
        });
        if (res.ok) { success++; }
        else {
          failed++;
          const err = await res.text();
          errors.push({ row: i + 1, title: previewPayloads[i].title || previewPayloads[i].name || previewPayloads[i].part_number, error: err.substring(0, 100) });
        }
      } catch (e) {
        failed++;
        errors.push({ row: i + 1, title: '—', error: e.message });
      }
    }
    setImportResults({ success, failed, errors });
    setStep('done');
    setImporting(false);
  };

  const reset = () => {
    setImportType(null); setRawData([]); setFileColumns([]);
    setMapping({}); setFileName(''); setStep('select');
    setImportResults({ success: 0, failed: 0, errors: [] });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ color: theme.text, fontSize: 20, fontWeight: 700, margin: 0 }}>Bulk Import</h2>
        {step !== 'select' && (
          <button onClick={reset} style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer' }}>← Start Over</button>
        )}
      </div>

      {/* Step 1: Select Import Type */}
      {step === 'select' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 12 }}>
          {Object.entries(IMPORT_TYPES).map(([key, cfg]) => (
            <div key={key} onClick={() => { setImportType(key); setStep('upload'); }}
              style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = cfg.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.transform = 'translateY(0)'; }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>{cfg.icon}</div>
              <div style={{ color: theme.text, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{cfg.label}</div>
              <div style={{ color: theme.textDim, fontSize: 12, lineHeight: 1.5 }}>{cfg.description}</div>
              <div style={{ marginTop: 14 }}>
                <div style={{ color: theme.textDim, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Expected Columns</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                  {cfg.expectedColumns.slice(0, 5).map(c => (
                    <span key={c} style={{ background: cfg.color + '18', color: cfg.color, fontSize: 9, padding: '2px 6px', borderRadius: 4 }}>{c}</span>
                  ))}
                  {cfg.expectedColumns.length > 5 && <span style={{ color: theme.textDim, fontSize: 9 }}>+{cfg.expectedColumns.length - 5} more</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 2: Upload File */}
      {step === 'upload' && cfg && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
          <div style={{ color: cfg.color, fontSize: 48 }}>{cfg.icon}</div>
          <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>Upload {cfg.label} Spreadsheet</div>
          <div style={{ color: theme.textDim, fontSize: 13 }}>Supports .xlsx, .xls, and .csv files</div>
          <div onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
            style={{ border: `2px dashed ${cfg.color}66`, borderRadius: 12, padding: '40px 60px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', background: cfg.color + '08' }}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])} />
            <div style={{ color: cfg.color, fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Drop file here or click to browse</div>
            <div style={{ color: theme.textDim, fontSize: 12 }}>Your column headers will be auto-matched</div>
          </div>
          <div style={{ color: theme.textDim, fontSize: 11, marginTop: 8 }}>
            Expected columns: {cfg.expectedColumns.join(' · ')}
          </div>
        </div>
      )}

      {/* Step 3: Column Mapping */}
      {step === 'map' && cfg && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ color: theme.text, fontSize: 16, fontWeight: 600 }}>Map Columns</div>
                <div style={{ color: theme.textDim, fontSize: 12, marginTop: 2 }}>{fileName} — {rawData.length} rows found</div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <span style={{ color: theme.green, fontSize: 12 }}>{Object.keys(mapping).length} mapped</span>
                <span style={{ color: theme.textDim, fontSize: 12 }}>/ {fileColumns.length} columns</span>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', gap: '8px 12px', alignItems: 'center' }}>
              <div style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>Your Column</div>
              <div />
              <div style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>Maps To</div>
              {fileColumns.map(fc => (
                <React.Fragment key={fc}>
                  <div style={{ padding: '8px 12px', background: theme.bg, borderRadius: 6, color: theme.text, fontSize: 13 }}>{fc}</div>
                  <div style={{ color: mapping[fc] ? theme.green : theme.textDim, textAlign: 'center', fontSize: 16 }}>{mapping[fc] ? '→' : '·'}</div>
                  <select value={mapping[fc] || ''} onChange={e => updateMapping(fc, e.target.value)}
                    style={{ ...iStyle, appearance: 'auto', borderColor: mapping[fc] ? theme.green + '66' : theme.border }}>
                    <option value="">— Skip this column —</option>
                    {Object.entries(cfg.fieldMap).map(([expCol, field]) => (
                      <option key={field.target} value={field.target}>{expCol}{field.required ? ' *' : ''}</option>
                    ))}
                  </select>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 20 }}>
            <div style={{ color: theme.text, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
              Preview — {previewPayloads.length} valid records (of {rawData.length} rows)
            </div>
            {previewPayloads.length === 0 ? (
              <div style={{ color: theme.yellow, fontSize: 13, padding: 16, textAlign: 'center' }}>No valid records found. Make sure required fields are mapped.</div>
            ) : (
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>{Object.keys(previewPayloads[0]).map(k => (
                      <th key={k} style={{ color: theme.textDim, fontSize: 10, textTransform: 'uppercase', padding: '8px 10px', textAlign: 'left', borderBottom: `1px solid ${theme.border}`, position: 'sticky', top: 0, background: theme.surface }}>{k.replace(/_/g, ' ')}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {previewPayloads.slice(0, 20).map((row, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${theme.border}08` }}>
                        {Object.values(row).map((v, j) => (
                          <td key={j} style={{ color: theme.textMuted, padding: '6px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {v === null ? '—' : String(v).substring(0, 60)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewPayloads.length > 20 && <div style={{ color: theme.textDim, fontSize: 11, padding: 8, textAlign: 'center' }}>...and {previewPayloads.length - 20} more rows</div>}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16, paddingBottom: 20 }}>
            <button onClick={reset} style={{ padding: '10px 20px', borderRadius: 8, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={handleImport} disabled={previewPayloads.length === 0}
              style={{ padding: '10px 24px', borderRadius: 8, background: previewPayloads.length > 0 ? cfg.color : theme.textDim, color: '#fff', border: 'none', cursor: previewPayloads.length > 0 ? 'pointer' : 'not-allowed', fontSize: 14, fontWeight: 600 }}>
              Import {previewPayloads.length} Records
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Importing */}
      {step === 'importing' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <div style={{ fontSize: 48, animation: 'spin 1s linear infinite' }}>⏳</div>
          <div style={{ color: theme.text, fontSize: 18, fontWeight: 600 }}>Importing...</div>
          <div style={{ color: theme.textDim, fontSize: 13 }}>Processing {previewPayloads.length} records</div>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Step 5: Results */}
      {step === 'done' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <div style={{ fontSize: 56 }}>{importResults.failed === 0 ? '✅' : '⚠️'}</div>
          <div style={{ color: theme.text, fontSize: 22, fontWeight: 700 }}>Import Complete</div>
          <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: theme.green, fontSize: 36, fontWeight: 700 }}>{importResults.success}</div>
              <div style={{ color: theme.textDim, fontSize: 12 }}>Imported</div>
            </div>
            {importResults.failed > 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: theme.red, fontSize: 36, fontWeight: 700 }}>{importResults.failed}</div>
                <div style={{ color: theme.textDim, fontSize: 12 }}>Failed</div>
              </div>
            )}
          </div>
          {importResults.errors.length > 0 && (
            <div style={{ background: theme.surface, border: `1px solid ${theme.red}44`, borderRadius: 10, padding: 16, maxWidth: 500, maxHeight: 200, overflow: 'auto', marginTop: 12 }}>
              <div style={{ color: theme.red, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Errors:</div>
              {importResults.errors.map((e, i) => (
                <div key={i} style={{ color: theme.textMuted, fontSize: 11, marginBottom: 4 }}>
                  Row {e.row}: <span style={{ color: theme.text }}>{e.title}</span> — <span style={{ color: theme.red }}>{e.error}</span>
                </div>
              ))}
            </div>
          )}
          <button onClick={reset} style={{ padding: '10px 24px', borderRadius: 8, background: theme.accent, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginTop: 16 }}>Import More</button>
        </div>
      )}
    </div>
  );
}
